import type { StorefrontUpsell } from '@xshop/shared';
import React, { createContext, useContext, useState } from 'react';
import { getActiveStore, type MerchandiseQuote, type StorefrontProduct } from '../api/client';
import { OnePageCheckout } from '../components/OnePageCheckout';
import { useCodForm } from '../components/useCodForm';
import { readShellOrder, type ShellOrder } from '../themeHost/themeMount';
import { mergePendingLines, orderableVariant, setLineQuantity, type PendingLine } from './pendingLine';
import { useCartLines, type CartLines } from './useCartLines';
import { useRecoveredCart } from './useRecoveredCart';
import { SharedCodeNotice } from './SharedCodeNotice';
import { CheckoutLayout } from './CheckoutLayout';
import { sampleCommerce } from './sampleCart';
import { usePageLineCart, type PageLine } from './usePageLineCart';
import { useCheckoutAnswers } from './useCheckoutAnswers';
import { useOrderNote } from './useOrderNote';
import { orderedLines, type PlacedCart } from './orderedLines';
import { offerSavings } from './offerSavings';
import { trackAdded } from './commerceEvents';
import { CartStatus, EmptyCart } from './CartStates';
import { OrderConfirmation } from './OrderConfirmation';
import { isThemeEditor } from '../editor/editorVisit';
import { isThemePreview } from '../api/previewVisit';
import './commerce.css';
import type { CheckoutLanguage } from '../components/checkoutLabels';
import { shopWords } from './shopWords';

/** Where the cart stands: shut, open on its lines, or open on the order form. */
export type CartView = 'closed' | 'cart' | 'checkout';

export type Commerce = {
  lines: PendingLine[]; products: StorefrontProduct[]; quote: MerchandiseQuote | null;
  addMany: (items: { product: StorefrontProduct; variantId: string; quantity: number }[]) => void;
  remove: (variantId: string) => void; setQuantity: (variantId: string, quantity: number) => void; retry: () => void;
  line: PendingLine | null; product: StorefrontProduct | null; order: ShellOrder | null;
  offer: StorefrontUpsell | null; acceptOffer: (offer: StorefrontUpsell | null) => void; navigate: (page: string, replace?: boolean) => void;
  error: string; loading: boolean; currency: string; direction: 'ltr' | 'rtl'; language: CheckoutLanguage | null;
  add: (product: StorefrontProduct, variantId: string, quantity: number) => void;
  view: CartView; show: (view: CartView) => void;
  /** The product the quick view panel shows, or null while it is closed; `peek` opens and closes it. */
  quick: StorefrontProduct | null; peek: (product: StorefrontProduct | null) => void;
  /** Whether the search panel is open; `search` opens and closes it. */
  searching: boolean; search: (open: boolean) => void;
  /** Whether search suggests products as the shopper types, as the theme's Search setting says. */
  predictive: boolean;
  open: () => void; clear: () => void; complete: (raw: unknown) => void;
  /** The note the shopper wrote in the cart, sent with the order. */
  note: string; setNote: (note: string) => void;
  /** What Buy it now is ordering, apart from the cart, as Shopify's does: the cart is never touched by it. */
  direct: PageLine | null;
  /** Orders one product at once, on the checkout page or the form over the page, leaving the cart as it was. */
  buyNow: (line: PageLine, onItsOwnPage: boolean) => void;
  /** The cart's own Check out, which orders the cart and drops whatever Buy it now was holding. */
  checkoutCart: (onItsOwnPage: boolean) => void;
  /** Finishes an order placed for lines other than the cart's, keeping the cart. */
  completeLine: (raw: unknown, placed: PlacedCart) => void;
};
const Context = createContext<Commerce | null>(null);
// The commerce state belongs to this store visit, outside repeated product scopes.
export const useCommerce = () => useContext(Context);

/** The cart's changes to its own lines, which the provider spreads into what it publishes. */
type CartActions = Pick<Commerce, 'addMany' | 'add' | 'remove' | 'setQuantity'>;

// Moves the visit to an address of its own without a reload, at the top of the page it opens.
function goToAddress(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

// Moves the visit to the checkout's own address without a reload, keeping a preview's settings in the address.
function goToCheckoutPage(): void {
  const url = new URL(window.location.href);
  url.pathname = '/checkout';
  url.searchParams.delete('page');
  goToAddress(`${url.pathname}${url.search}`);
}

// Moves the visit to another theme page without a reload, at its top as a new page opens; the address bar is the only router.
function goToPage(page: string, replace = false): void {
  const url = new URL(window.location.href);
  url.searchParams.set('page', page);
  // Replacing is for a page that steps aside for the next one, so Back never lands on it again.
  if (replace) window.history.replaceState({}, '', url);
  else window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

// Builds the changes a shopper makes to the lines: adding refuses what cannot be ordered, opens the cart and tells the pixels.
function cartActions(cart: CartLines, forgetOffer: () => void, openCart: () => void, currency: string): CartActions {
  // Adds the products given, refusing them all when one cannot be ordered.
  const addMany: Commerce['addMany'] = (items) => {
    if (isThemeEditor() || isThemePreview() || !items.length) return;
    if (items.some(({ product, variantId, quantity }) => !orderableVariant(product, { slug: product.slug, variantId, quantity }))) return;
    const next = mergePendingLines(cart.lines, items.map(({ product, variantId, quantity }) => ({ slug: product.slug, variantId, quantity })));
    openCart();
    if (!next) return cart.fail('The cart quantity limit has been reached.');
    forgetOffer();
    cart.setLines(next, items.map(({ product }) => product));
    trackAdded(items, currency);
  };
  return {
    addMany,
    add: (product, variantId, quantity) => addMany([{ product, variantId, quantity }]),
    remove: (id) => { forgetOffer(); cart.setLines(cart.lines.filter((row) => row.variantId !== id)); },
    setQuantity: (id, quantity) => cart.setLines(setLineQuantity(cart.lines, id, quantity))
  };
}

/** What the shell tells the cart: money and reading direction, the store's language, and how the theme opens the cart. */
type ShopCommerceProps = { currency: string; direction: 'ltr' | 'rtl'; language: CheckoutLanguage | null; cartAs?: 'drawer' | 'page'; predictive?: boolean; checkoutOnPage?: boolean };

// Opens the cart the way the theme says: its drawer over the page, or the /cart page itself.
function cartOpener(cartAs: 'drawer' | 'page', setView: (view: CartView) => void): () => void {
  if (cartAs === 'drawer') return () => setView('cart');
  return () => { setView('closed'); if (window.location.pathname.replace(/\/$/, '') !== '/cart') goToAddress('/cart'); };
}

// Builds Buy it now: one product ordered on its own — never added to the cart, so pressing it again never piles up.
function buyNowAction(setDirect: (line: PageLine | null) => void, forget: () => void, openCheckout: (onItsOwnPage: boolean) => void): Commerce['buyNow'] {
  return (line, onItsOwnPage) => {
    if (isThemeEditor() || isThemePreview()) return;
    if (!orderableVariant(line.product, { slug: line.product.slug, variantId: line.variantId, quantity: line.quantity })) return;
    forget();
    setDirect(line);
    openCheckout(onItsOwnPage);
  };
}

// The cart keeps product choices; the server prices all its lines using the same rules as checkout.
export const ShopCommerce: React.FC<React.PropsWithChildren<ShopCommerceProps>> = ({ children, currency, direction, language, cartAs = 'drawer', predictive = true, checkoutOnPage = false }) => {
  const cart = useCartLines(`xshop:pending:${getActiveStore()?.slug ?? ''}`);
  const [offer, setOffer] = useState<StorefrontUpsell | null>(null);
  const [order, setOrder] = useState<ShellOrder | null>(null);
  const [view, setView] = useState<CartView>('closed');
  const [quick, setQuick] = useState<StorefrontProduct | null>(null);
  const [searching, search] = useState(false);
  const [note, setNote] = useOrderNote(getActiveStore()?.slug ?? '');
  const [direct, setDirect] = useState<PageLine | null>(null);
  const line = cart.lines[0] ?? null;
  // Empties the cart and what it carried: its error, an accepted offer, the note.
  const clear = () => { cart.setLines([]); cart.fail(''); setOffer(null); setNote(''); };
  // Opens the cart; opening it, or changing it, means the cart is what is ordered next, not what Buy it now held.
  const openCart = () => { setDirect(null); cartOpener(cartAs, setView)(); };
  // Forgets a placed order, an accepted offer and Buy it now's line, as any change to what is ordered does.
  const forget = () => { setOrder(null); setOffer(null); setDirect(null); };
  // Shows the placed order's own address, listing what it held: the cart emptied after its order, kept after Buy it now's.
  const finish = (raw: unknown, placed: PlacedCart, ofCart: boolean) => {
    const shell = readShellOrder(raw);
    setOrder(shell && { ...shell, lines: orderedLines(placed) });
    if (ofCart) clear(); else setDirect(null);
    setView('closed');
    goToAddress('/thank-you');
  };
  // Opens the order form: the checkout page of a store that sells on its own page, else the form over the page.
  const openCheckout = (onItsOwnPage: boolean) => {
    if (!onItsOwnPage) return setView('checkout');
    setView('closed');
    goToCheckoutPage();
  };
  useRecoveredCart(cart.setLines, cart.fail, openCart);
  const value: Commerce = {
    lines: cart.lines, products: cart.products, quote: cart.quote, loading: cart.loading, error: cart.error, retry: cart.retry,
    ...cartActions(cart, forget, openCart, currency),
    line, product: cart.products.find((row) => row.slug === line?.slug) ?? null, order, offer, acceptOffer: setOffer,
    currency, direction, language, clear, view, open: openCart, quick, peek: setQuick, searching, search, predictive, note, setNote,
    show: (next) => { if (next === 'cart') setDirect(null); setView(next); },
    navigate: (page, replace) => { setView('closed'); goToPage(page, replace); },
    direct,
    buyNow: buyNowAction(setDirect, forget, openCheckout),
    checkoutCart: (onItsOwnPage) => { setDirect(null); openCheckout(onItsOwnPage || checkoutOnPage); },
    complete: (raw) => finish(raw, cart, true),
    completeLine: (raw, placed) => finish(raw, placed, false)
  };
  return <Context.Provider value={value}>{children}<SharedCodeNotice language={language ?? null} /></Context.Provider>;
};

/** The form's own heading, silenced where the drawer or the page already says what this is. */
const NO_HEADING = { heading: '' };

type CheckoutFormProps = { commerce: Commerce; showHeading: boolean; preview: boolean; fromCart: boolean };

// Draws the order form for the priced lines; while a new price is read it stays, its button paused, its answers kept.
const CheckoutForm: React.FC<CheckoutFormProps> = ({ commerce, showHeading, preview, fromCart }) => {
  const config = useCodForm();
  const answers = useCheckoutAnswers(commerce.offer?.id ?? null);
  const { lines, quote, currency, direction, language } = commerce;
  const first = quote?.lines[0];
  if (!quote || !first || commerce.error) return null;
  const quantity = lines.reduce((sum, line) => sum + line.quantity, 0);
  return <>
    {fromCart && lines.length === 1 && <button type="button" className="xe-cart-link" onClick={() => commerce.navigate('upsell')}>{shopWords(commerce.language).viewOffers}</button>}
    <OnePageCheckout key={JSON.stringify(quote.lines.map((row) => [row.variantId, row.quantity]))} config={config} productId={preview ? '' : first.productId}
      variantId={first.variantId} quantity={quantity} lineTotal={quote.subtotal} preview={preview} busy={commerce.loading} answers={answers}
      lineItems={quote.lines.map(({ productId, variantId, quantity: count }) => ({ productId, variantId, quantity: count }))}
      pricedLines={quote.lines.map((row) => ({ productId: row.productId, quantity: row.quantity, lineTotal: Number(row.totalPrice) }))}
      offerSavings={offerSavings(quote, 'Pack')}
      currency={currency} direction={direction} language={language} offerOverride={commerce.offer} onSuccess={commerce.complete} orderNote={commerce.note}
      labels={showHeading ? undefined : NO_HEADING} />
  </>;
};

/** What the form orders, and where that came from. */
type CheckoutCart = { commerce: Commerce; preview: boolean; fromCart: boolean };

// Picks what the form orders: a preview's samples, what Buy it now holds, the page's own choice when the cart is empty,
// or the shopper's cart.
function useCheckoutCart(samples: readonly StorefrontProduct[], pageLine: PageLine | null, inDrawer: boolean): CheckoutCart | null {
  const live = useCommerce();
  const pageCart = usePageLineCart(live, live?.direct ?? (live?.lines.length || inDrawer ? null : pageLine));
  if (!live) return null;
  if (isThemeEditor() || isThemePreview()) return { commerce: sampleCommerce(live, samples), preview: true, fromCart: false };
  return { commerce: pageCart ?? live, preview: false, fromCart: !pageCart };
}

type CheckoutContentProps = { inDrawer?: boolean; samples?: readonly StorefrontProduct[]; showHeading?: boolean; pageLine?: PageLine | null };

// Draws the server-priced order form in the drawer or a checkout section; a preview runs it on the theme's samples.
export const CheckoutContent: React.FC<CheckoutContentProps> = ({ inDrawer = false, samples = [], showHeading = true, pageLine = null }) => {
  const cart = useCheckoutCart(samples, pageLine, inDrawer);
  if (!cart) return null;
  const { commerce, preview, fromCart } = cart;
  if (commerce.order) return <OrderConfirmation />;
  if (!commerce.lines.length) return <EmptyCart />;
  const form = <><CartStatus commerce={commerce} /><CheckoutForm commerce={commerce} showHeading={showHeading && !inDrawer} preview={preview} fromCart={fromCart && !preview} /></>;
  return inDrawer ? <div className="xe-checkout-content">{form}</div> : <CheckoutLayout commerce={commerce}>{form}</CheckoutLayout>;
};
